import { useEffect, useRef, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { Country } from "country-state-city";
import { TemplateFormData } from "../../../views/marketing-pages/templates";
import {
  ButtonType,
  Category,
  CtaButton,
  CtaButtonType,
  Language,
} from "../../../views/marketing-pages/templates/types";
import Input from "../../input";
import { RadioGroup } from "../../radio";
import Select from "../../select";
import { HeaderSection } from "./header-section";
import { MessageBodySection } from "./message-section";
import { MessageVariablesSection } from "./message-variables-section";
import { CharCount, SectionLabel } from "./utiz";
import useDefaultCountryCode from "../../hook/use-default-country-code";

// ─── Dial code list for the CTA "Call Phone Number" button ────────────────
const normalizeDialCode = (phoneCode?: string | number | null) => {
  if (!phoneCode) return "+234";
  const code = String(phoneCode);
  return code.startsWith("+") ? code : `+${code}`;
};

const countryCodeOptions = Country.getAllCountries()
  .map((country) => ({
    value: normalizeDialCode(country.phonecode),
    label: `${normalizeDialCode(country.phonecode)} ${country.isoCode}`,
    isoCode: country.isoCode,
  }))
  .sort((a, b) => a.value.localeCompare(b.value));

const getCountryByDialCode = (dialCode?: string | number | null) =>
  countryCodeOptions.find((c) => c.value === normalizeDialCode(dialCode));

// Splits a stored "+234801..." style number into its dial code + local part,
// picking the longest matching dial code so e.g. "+1" doesn't shadow "+123".
const splitPhoneNumber = (phone: string, fallbackDialCode: string) => {
  const normalized = (phone || "").trim();
  const matched = [...countryCodeOptions]
    .sort((a, b) => b.value.length - a.value.length)
    .find((c) => normalized.startsWith(c.value));
  const countryCode = matched?.value || fallbackDialCode;
  return {
    countryCode,
    localNumber: normalized.startsWith(countryCode) ? normalized.slice(countryCode.length) : normalized,
  };
};

const CountryCodeSelect: React.FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = getCountryByDialCode(value);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex h-[45px] items-center gap-1.5 rounded border border-gray-200 dark:border-gray-600 px-2.5 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-transparent"
      >
        {selected && (
          <img
            src={`https://flagcdn.com/24x18/${selected.isoCode.toLowerCase()}.png`}
            alt=""
            className="h-3.5 w-5 rounded-sm object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
        )}
        <span>{value}</span>
        <ChevronDown size={14} className={`text-[#7B7B7B] font-semibold dark:text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 max-h-56 w-56 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
          {countryCodeOptions.map((option) => (
            <button
              key={option.isoCode}
              type="button"
              onClick={() => { onChange(option.value); setOpen(false); }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
                option.value === value ? "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400" : "text-gray-700 dark:text-gray-200"
              }`}
            >
              <img
                src={`https://flagcdn.com/24x18/${option.isoCode.toLowerCase()}.png`}
                alt=""
                className="h-3.5 w-5 rounded-sm object-cover"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              />
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CtaPhoneNumberInput: React.FC<{
  phoneNumber: string;
  onChange: (phoneNumber: string) => void;
}> = ({ phoneNumber, onChange }) => {
  const defaultCountryCode = useDefaultCountryCode();
  const { countryCode, localNumber } = splitPhoneNumber(phoneNumber, defaultCountryCode);

  return (
    <div className="flex gap-2">
      <CountryCodeSelect
        value={phoneNumber ? countryCode : defaultCountryCode}
        onChange={(nextCode) => onChange(`${nextCode}${localNumber}`)}
      />
      <div className="flex-1">
        <Input
          value={localNumber}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange(`${phoneNumber ? countryCode : defaultCountryCode}${e.target.value.replace(/\D/g, "")}`)
          }
          placeholder="8012345678"
        />
      </div>
    </div>
  );
};

const ButtonsSection: React.FC<{ formData: TemplateFormData; onChange: (u: Partial<TemplateFormData>) => void }> = ({ formData, onChange }) => {
  const addQuickReply = () =>
    onChange({ quickReplyButtons: [...formData.quickReplyButtons, { id: String(Date.now()), label: '' }] });

  const updateQuickReply = (id: string, label: string) =>
    onChange({ quickReplyButtons: formData.quickReplyButtons.map((b) => (b.id === id ? { ...b, label } : b)) });

  const removeQuickReply = (id: string) =>
    onChange({ quickReplyButtons: formData.quickReplyButtons.filter((b) => b.id !== id) });

  // Helper function to handle input change
  const handleQuickReplyChange = (id: string, eventOrValue: React.ChangeEvent<HTMLInputElement> | string) => {
    const value = typeof eventOrValue === 'string' ? eventOrValue : eventOrValue.target.value;
    updateQuickReply(id, value);
  };

  return (
    <div>
      <SectionLabel label="Buttons" optional />
      <RadioGroup
        name="buttonType"
        options={[{ label: 'None', value: 'none' }, { label: 'Call to Action', value: 'call_to_action' }, { label: 'Quick Reply', value: 'quick_reply' }]}
        value={formData.buttonType}
        onChange={(v) => onChange({ buttonType: v as ButtonType })}
      />
      {formData.buttonType === 'quick_reply' && (
        <div className="mt-3 space-y-2">
          {formData.quickReplyButtons.map((btn) => (
            <div key={btn.id} className="flex items-center gap-2">
              <Input
                value={btn.label}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleQuickReplyChange(btn.id, e)}
                placeholder="Button label"
              />
              {formData.quickReplyButtons.length > 1 && (
                <button type="button" onClick={() => removeQuickReply(btn.id)} className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-red-500 transition-colors flex-shrink-0">
                  <X size={16} />
                </button>
              )}
            </div>
          ))}
          {formData.quickReplyButtons.length < 3 && (
            <button type="button" onClick={addQuickReply} className="text-sm text-indigo-600 dark:text-indigo-400 font-medium hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors flex items-center gap-1">
              + Add More
            </button>
          )}
        </div>
      )}
      {formData.buttonType === 'call_to_action' && (
        <CtaButtonsEditor formData={formData} onChange={onChange} />
      )}
    </div>
  );
};

// Meta's stated cap: up to 2 "Visit Website" (URL), 1 "Call Phone Number",
// 1 "Copy Offer Code" — verified 2026-08-18. VOICE_CALL ("Call on WhatsApp")
// isn't given an explicit cap in what's documented publicly; treated as max
// 1 here too, consistent with the other single-purpose types.
const CTA_TYPE_CAPS: Record<CtaButtonType, number> = { URL: 2, PHONE_NUMBER: 1, VOICE_CALL: 1, COPY_CODE: 1 };
const CTA_TYPE_ORDER: CtaButtonType[] = ['URL', 'PHONE_NUMBER', 'VOICE_CALL', 'COPY_CODE'];
const CTA_MAX_TOTAL = 4;

const CtaButtonsEditor: React.FC<{ formData: TemplateFormData; onChange: (u: Partial<TemplateFormData>) => void }> = ({ formData, onChange }) => {
  const ctaButtons = formData.ctaButtons || [];

  const updateCta = (id: string, patch: Partial<CtaButton>) =>
    onChange({ ctaButtons: ctaButtons.map((b) => (b.id === id ? { ...b, ...patch } : b)) });

  const removeCta = (id: string) =>
    onChange({ ctaButtons: ctaButtons.filter((b) => b.id !== id) });

  const countByType = (type: CtaButtonType) => ctaButtons.filter((b) => b.type === type).length;
  const canAddType = (type: CtaButtonType) => countByType(type) < CTA_TYPE_CAPS[type];

  const addCta = () => {
    if (ctaButtons.length >= CTA_MAX_TOTAL) return;
    const nextType = CTA_TYPE_ORDER.find(canAddType);
    if (!nextType) return;
    onChange({
      ctaButtons: [
        ...ctaButtons,
        {
          id: String(Date.now()),
          type: nextType,
          // Confirmed shape (2026-08-18): COPY_CODE does send `text` — pre-fill
          // WhatsApp's own conventional label rather than leaving it blank.
          text: nextType === 'COPY_CODE' ? 'Copy offer code' : '',
          url: '',
          phoneNumber: '',
          urlType: 'static',
          example: '',
        },
      ],
    });
  };

  return (
    <div className="mt-3 space-y-3">
      <p className="text-xs text-gray-500 dark:text-gray-400 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-800 rounded p-2.5">
        Call to Action buttons let customers call your phone, call you on WhatsApp, visit your website, or copy an offer code.
      </p>
      {ctaButtons.map((btn) => (
        <div key={btn.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <select
              value={btn.type}
              onChange={(e) => updateCta(btn.id, { type: e.target.value as CtaButtonType })}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100"
            >
              <option value="URL">Visit Website (URL)</option>
              <option value="PHONE_NUMBER">Call Phone Number</option>
              <option value="VOICE_CALL">Call on WhatsApp</option>
              <option value="COPY_CODE">Copy Offer Code</option>
            </select>
            {ctaButtons.length > 1 && (
              <button type="button" onClick={() => removeCta(btn.id)} className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-red-500 transition-colors flex-shrink-0">
                <X size={16} />
              </button>
            )}
          </div>

          <Input
            value={btn.text}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateCta(btn.id, { text: e.target.value })}
            placeholder={btn.type === 'COPY_CODE' ? 'Button label, e.g. Copy offer code' : 'Button label, e.g. Visit Website'}
          />

          {btn.type === 'URL' && (
            <>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                  <input
                    type="radio"
                    checked={(btn.urlType || 'static') === 'static'}
                    onClick={(e) => { e.preventDefault(); updateCta(btn.id, { urlType: 'static' }); }}
                    onChange={() => {}}
                    className="accent-indigo-600"
                  />
                  Static URL
                </label>
                <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                  <input
                    type="radio"
                    checked={btn.urlType === 'dynamic'}
                    onClick={(e) => { e.preventDefault(); updateCta(btn.id, { urlType: 'dynamic' }); }}
                    onChange={() => {}}
                    className="accent-indigo-600"
                  />
                  Dynamic URL
                </label>
              </div>
              <Input
                value={btn.url || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateCta(btn.id, { url: e.target.value })}
                placeholder={btn.urlType === 'dynamic' ? 'https://example.com/{{1}}' : 'https://example.com'}
              />
              {btn.urlType === 'dynamic' && (
                <Input
                  value={btn.example || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateCta(btn.id, { example: e.target.value })}
                  placeholder="Example URL, e.g. https://example.com/order123"
                />
              )}
            </>
          )}

          {btn.type === 'PHONE_NUMBER' && (
            <CtaPhoneNumberInput
              phoneNumber={btn.phoneNumber || ''}
              onChange={(phoneNumber) => updateCta(btn.id, { phoneNumber })}
            />
          )}

          {btn.type === 'COPY_CODE' && (
            <Input
              value={btn.example || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateCta(btn.id, { example: e.target.value })}
              placeholder="Example offer code, e.g. SUMMER2024"
            />
          )}
        </div>
      ))}
      {ctaButtons.length < CTA_MAX_TOTAL && CTA_TYPE_ORDER.some(canAddType) && (
        <button type="button" onClick={addCta} className="text-sm text-indigo-600 dark:text-indigo-400 font-medium hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors flex items-center gap-1">
          + Add Button
        </button>
      )}
    </div>
  );
};

const FooterSection: React.FC<{ formData: TemplateFormData; onChange: (u: Partial<TemplateFormData>) => void }> = ({ formData, onChange }) => {
  // Helper function to handle footer change
  const handleFooterChange = (eventOrValue: React.ChangeEvent<HTMLInputElement> | string) => {
    const value = typeof eventOrValue === 'string' ? eventOrValue : eventOrValue.target.value;
    onChange({ footer: value });
  };

  return (
    <div>
      <SectionLabel label="Footer" optional />
      <Input
        value={formData.footer}
        onChange={handleFooterChange}
        placeholder="Enter footer text"
      />
      <CharCount current={formData.footer.length} max={60} />
    </div>
  );
};

export const WhatsAppForm: React.FC<{
  formData: TemplateFormData;
  onChange: (u: Partial<TemplateFormData>) => void;
  wabaAccounts?: Array<{ value: string; label: string }>;
  readOnly?: boolean;
}> = ({ formData, onChange, wabaAccounts = [] }) => {
  const wabaOptions = wabaAccounts.length > 0
    ? wabaAccounts
    : [{ label: 'Select a WhatsApp Business Account', value: '' }];

  const handleWhatsAppAccountChange = (value: string) => {
    onChange({ whatsappBusinessAccount: value });
  };

  const handleCategoryChange = (value: string) => {
    onChange({ category: value as Category });
  };

  const handleLanguageChange = (value: string) => {
    onChange({ language: value as Language });
  };

  return (
    <div className="space-y-6">
      <div>
        <SectionLabel label="WhatsApp Business Account" />
        <Select
          value={formData.whatsappBusinessAccount}
          onChange={handleWhatsAppAccountChange}
          options={wabaOptions}
          placeholder="Select a WhatsApp Business Account"

        />

        {wabaAccounts.length === 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            No WhatsApp Business Accounts found. Please create a template first or contact support.
          </p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <SectionLabel label="Category" />
          <Select
            value={formData.category}
            onChange={handleCategoryChange}
            options={[
              { label: 'Marketing', value: 'Marketing' },
              { label: 'Authentication', value: 'Authentication' },
              { label: 'Utility', value: 'Utility' }
            ]}
          />
        </div>
        <div>
          <SectionLabel label="Language" />
          <Select
            value={formData.language}
            onChange={handleLanguageChange}
            options={[
              { label: 'English (US)', value: 'English (US)' },
              { label: 'English (UK)', value: 'English (UK)' },
              { label: 'Spanish', value: 'Spanish' },
              { label: 'French', value: 'French' }
            ]}
          />
        </div>
      </div>
      <hr className="border-gray-100 dark:border-gray-800" />
      <HeaderSection formData={formData} onChange={onChange} />
      <hr className="border-gray-100 dark:border-gray-800" />
      <MessageBodySection formData={formData} onChange={onChange} />
      <MessageVariablesSection formData={formData} onChange={onChange} />
      <hr className="border-gray-100 dark:border-gray-800" />
      <ButtonsSection formData={formData} onChange={onChange} />
      <hr className="border-gray-100 dark:border-gray-800" />
      <FooterSection formData={formData} onChange={onChange} />
    </div>
  );
};